import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useCustomer from '../../hooks/useCustomer';
import useWarranty from '../../hooks/useWarranty';
import Card from '../../common/Card';
import Button from '../../common/Button';
import Loading from '../../common/Loading';
import ErrorMessage from '../../common/ErrorMessage';
import ConfirmDialog from '../../common/ConfirmDialog';
import WarrantyCard from '../../warranty/WarrantyCard';
import { formatDate } from '../../utils/formatters';
import { getCsrfToken } from '../../../utils/csrfUtils';
import './styles.css';

/**
 * 客戶詳情組件
 * 用於顯示客戶詳細信息
 * 
 * @param {Object} props - 組件屬性
 * @param {string} props.token - 認證令牌
 * @param {Object} props.user - 用戶信息
 * @param {boolean} props.warrantyView - 是否顯示保修單視圖
 * @returns {JSX.Element} 客戶詳情組件
 */
function CustomerDetail({ token, user, warrantyView = false }) {
    const { customerId } = useParams();
    const navigate = useNavigate();

    // 使用自定義Hook
    const {
        getCustomerById,
        customerLoading,
        customerError
    } = useCustomer(token);

    // 本地狀態
    const [customer, setCustomer] = useState(null);
    const [customerWarranties, setCustomerWarranties] = useState([]);
    const [warrantyLoading, setWarrantyLoading] = useState(false);
    const [warrantyError, setWarrantyError] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    // 加載客戶數據
    useEffect(() => {
        const loadCustomer = async () => {
            const customerData = await getCustomerById(customerId);
            setCustomer(customerData);
        };

        if (customerId) {
            loadCustomer();
        }
    }, [customerId, getCustomerById]);

    // 加載客戶的保養單
    useEffect(() => {
        const loadCustomerWarranties = async () => {
            if (customer && customer.id) {
                try {
                    const customerId = customer.id.split('/').pop();
                    const response = await fetch(`/api/customers/${customerId}/warranties`, {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });

                    if (!response.ok) {
                        throw new Error(`獲取保養單失敗: ${response.status}`);
                    }

                    const data = await response.json();
                    if (data.warranties && Array.isArray(data.warranties)) {
                        setCustomerWarranties(data.warranties);
                    } else if (Array.isArray(data)) {
                        setCustomerWarranties(data);
                    } else {
                        setCustomerWarranties([]);
                    }
                } catch (error) {
                    console.error('獲取客戶保養單時出錯:', error);
                }
            }
        };

        loadCustomerWarranties();
    }, [customer, token]);

    // 處理刪除客戶
    const handleDeleteCustomer = async () => {
        try {
            setDeleting(true);
            setDeleteError(null);

            // 獲取CSRF令牌
            const csrfToken = getCsrfToken();

            if (!csrfToken) {
                console.error('無法獲取CSRF令牌');
                throw new Error('安全驗證失敗，請重新登入後再試');
            }

            // console.log(`正在刪除客戶 ID: ${customerId}，CSRF令牌: ${csrfToken.substring(0, 10)}...`);

            const response = await fetch(`/api/customers/${customerId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-CSRF-Token': csrfToken
                },
                credentials: 'include' // 確保包含cookies
            });

            // 嘗試解析響應
            let responseData;
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                responseData = await response.json();
            } else {
                responseData = await response.text();
            }

            if (!response.ok) {
                console.error(`刪除客戶失敗: ${response.status} ${response.statusText}`, responseData);

                // 處理不同類型的錯誤響應
                if (typeof responseData === 'object' && responseData.error) {
                    if (Array.isArray(responseData.error)) {
                        throw new Error(`刪除客戶失敗: ${responseData.error.map(err => err.message).join(', ')}`);
                    } else if (typeof responseData.error === 'string') {
                        throw new Error(`刪除客戶失敗: ${responseData.error}`);
                    } else {
                        throw new Error(`刪除客戶失敗: ${JSON.stringify(responseData.error)}`);
                    }
                } else {
                    throw new Error(`刪除客戶失敗: ${response.status} ${response.statusText}`);
                }
            }

            // console.log('客戶刪除成功，正在導航回客戶列表');
            // 刪除成功，返回客戶列表
            navigate('/customers');
        } catch (error) {
            console.error('刪除客戶時出錯:', error);
            setDeleteError(error.message || '刪除客戶時出錯');
        } finally {
            setDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    // 渲染頁面標題
    const renderPageTitle = () => {
        if (warrantyView) {
            return (
                <div className="customer-detail-header">
                    <button
                        className="customer-detail-back-button"
                        onClick={() => navigate(`/customers/${customerId}`)}
                    >
                        &larr; 返回客戶詳情
                    </button>
                    <h2 className="customer-detail-title">
                        {customer ? `${customer.firstName} ${customer.lastName} 的保養單` : '客戶保養單'}
                    </h2>
                </div>
            );
        }

        return (
            <div className="customer-detail-header">
                <button
                    className="customer-detail-back-button"
                    onClick={() => navigate('/customers')}
                >
                    &larr; 返回客戶列表
                </button>
                <h2 className="customer-detail-title">
                    {customer ? `${customer.firstName} ${customer.lastName}` : '客戶詳情'}
                </h2>
            </div>
        );
    };

    // 渲染客戶信息卡片
    const renderCustomerInfoCard = () => {
        if (!customer) return null;

        return (
            <Card
                title="客戶信息"
                className="customer-detail-card"
                footer={
                    <div className="customer-detail-actions">
                        <Button
                            variant="secondary"
                            onClick={() => navigate(`/customers/${customerId}/edit`)}
                        >
                            編輯客戶
                        </Button>
                        {user?.role === 'admin' && (
                            <Button
                                variant="danger"
                                onClick={() => setShowDeleteConfirm(true)}
                            >
                                刪除客戶
                            </Button>
                        )}
                    </div>
                }
            >
                <div className="customer-detail-info">
                    <div className="customer-detail-row">
                        <div className="customer-detail-label">客戶ID:</div>
                        <div className="customer-detail-value">{customer.id.split('/').pop()}</div>
                    </div>

                    <div className="customer-detail-row">
                        <div className="customer-detail-label">NFC ID:</div>
                        <div className="customer-detail-value">{customer.nfc_id || '未設置'}</div>
                    </div>

                    <div className="customer-detail-row">
                        <div className="customer-detail-label">姓名:</div>
                        <div className="customer-detail-value">{customer.firstName} {customer.lastName}</div>
                    </div>

                    <div className="customer-detail-row">
                        <div className="customer-detail-label">電子郵件:</div>
                        <div className="customer-detail-value">{customer.email || '未設置'}</div>
                    </div>

                    <div className="customer-detail-row">
                        <div className="customer-detail-label">電話:</div>
                        <div className="customer-detail-value">{customer.phone || '未設置'}</div>
                    </div>

                    <div className="customer-detail-row">
                        <div className="customer-detail-label">等級:</div>
                        <div className="customer-detail-value">{customer.level || '普通'}</div>
                    </div>

                    <div className="customer-detail-section">
                        <h3 className="customer-detail-section-title">偏好設定</h3>

                        <div className="customer-detail-row">
                            <div className="customer-detail-label">性別:</div>
                            <div className="customer-detail-value">
                                {customer.gender ? (
                                    customer.gender.toLowerCase() === 'male' ? '男性' :
                                        customer.gender.toLowerCase() === 'female' ? '女性' : '其他'
                                ) : '未設置'}
                            </div>
                        </div>

                        <div className="customer-detail-row">
                            <div className="customer-detail-label">接收相關資訊:</div>
                            <div className="customer-detail-value">
                                {customer.receiveRelatedInfo === true ||
                                    customer.receiveRelatedInfo === 'true' ||
                                    customer.receive_info === 'Yes' ? '是' : '否'}
                            </div>
                        </div>
                    </div>
                </div>
            </Card>
        );
    };

    // 渲染保養單卡片
    const renderWarrantiesCard = () => {
        return (
            <Card
                title={warrantyView ? "保養單列表" : "保養單"}
                className="customer-detail-card"
                footer={
                    <div className="customer-detail-actions">
                        <Button
                            variant="primary"
                            onClick={() => navigate(`/customers/${customerId}/warranties/add`)}
                        >
                            新增保養單
                        </Button>
                    </div>
                }
            >
                {warrantyLoading ? (
                    <Loading text="正在加載保養單數據..." />
                ) : warrantyError ? (
                    <ErrorMessage message={warrantyError} />
                ) : customerWarranties.length > 0 ? (
                    <div className="customer-detail-warranties">
                        {customerWarranties.map(warranty => (
                            <WarrantyCard
                                key={warranty.id}
                                warranty={warranty}
                                formatDate={formatDate}
                                showActions={true}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="customer-detail-empty">
                        <p>此客戶尚未有保養單記錄</p>
                    </div>
                )}
            </Card>
        );
    };

    // 加載中狀態
    if (customerLoading) {
        return <Loading text="正在加載客戶數據..." />;
    }

    // 錯誤狀態
    if (customerError) {
        return (
            <ErrorMessage
                message={customerError}
                onRetry={() => window.location.reload()}
            />
        );
    }

    return (
        <div className="customer-detail-container">
            {/* 頁面標題 */}
            {renderPageTitle()}

            {/* 主要內容 */}
            {warrantyView ? (
                // 保養單視圖
                <>
                    {renderCustomerInfoCard()}
                    {renderWarrantiesCard()}
                </>
            ) : (
                // 客戶詳情視圖
                <>
                    {renderCustomerInfoCard()}
                    {renderWarrantiesCard()}
                </>
            )}

            {/* 刪除確認對話框 */}
            <ConfirmDialog
                isOpen={showDeleteConfirm}
                title="確認刪除"
                message="您確定要刪除此客戶嗎？此操作無法撤銷。"
                confirmText="刪除"
                cancelText="取消"
                onConfirm={handleDeleteCustomer}
                onCancel={() => setShowDeleteConfirm(false)}
                isLoading={deleting}
                error={deleteError}
            />
        </div>
    );
}

export default CustomerDetail; 